import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, Share2, Mail, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PNG_URL = `${BACKEND}/api/tutor/progress-card.png`;

export default function ProgressCardModal({ open, onClose }) {
  const [pngBlob, setPngBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(PNG_URL, { headers: { Authorization: `Bearer ${localStorage.getItem("tmo_token")}` }})
      .then((r) => r.blob())
      .then((b) => {
        setPngBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
      })
      .catch(() => toast.error("Couldn't render card"))
      .finally(() => setLoading(false));
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [open]);

  const download = () => {
    if (!pngBlob) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `teacher4me-progress-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const share = async () => {
    if (!pngBlob) return;
    const file = new File([pngBlob], "progress.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: "My Teacher4Me weekly progress",
          text: "Look how I've been doing on TeachMeOnline.ca this week!",
          files: [file],
        });
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.warn("native share failed", err);
        }
      }
    } else {
      // fallback: copy link
      try {
        await navigator.clipboard.writeText("https://teachmeonline.ca/tutor");
        setCopied(true);
        toast.success("Link copied. Paste it in your text!");
        setTimeout(() => setCopied(false), 2000);
      } catch { toast.info("Use the Download button and attach the image."); }
    }
  };

  const emailParent = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/api/tutor/progress-card/email-parent", { days: 7 });
      if (data.ok) toast.success(`Sent to ${data.sent_to}!`);
      else toast.error(data.error?.includes("parent email") ? "Add a parent email in onboarding first." : "Couldn't email — try Download instead.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send email.");
    } finally { setEmailing(false); }
  };

  if (!open) return null;
  return (
    <div data-testid="progress-card-modal"
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <div className="font-display font-bold text-lg">Your weekly progress card</div>
          <button data-testid="card-close" onClick={onClose} className="p-1.5 rounded hover:bg-[#F3F4F6]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          {loading && <div className="text-sm text-[#4B5563] text-center py-12" data-testid="card-loading">Rendering your card…</div>}
          {previewUrl && (
            <img data-testid="card-preview" src={previewUrl} alt="progress card"
              className="w-full rounded-xl border border-[#E5E7EB]" />
          )}
        </div>
        <div className="flex flex-wrap gap-2 p-4 border-t border-[#E5E7EB] bg-[#F9FAFB] sticky bottom-0">
          <button data-testid="card-download" onClick={download} disabled={!pngBlob}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Download
          </button>
          <button data-testid="card-share" onClick={share} disabled={!pngBlob}
            className="btn-outline disabled:opacity-50 inline-flex items-center gap-2">
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {copied ? "Copied" : "Share"}
          </button>
          <button data-testid="card-email-parent" onClick={emailParent} disabled={emailing || !pngBlob}
            className="btn-outline disabled:opacity-50 inline-flex items-center gap-2">
            <Mail className="w-4 h-4" /> {emailing ? "Sending…" : "Email to parent"}
          </button>
          <button data-testid="card-copy-link" onClick={() => {
            navigator.clipboard.writeText("https://teachmeonline.ca/tutor");
            setCopied(true); setTimeout(() => setCopied(false), 2000);
            toast.success("Link copied");
          }} className="btn-outline inline-flex items-center gap-2 text-xs ml-auto">
            <Copy className="w-3 h-3" /> Copy invite link
          </button>
        </div>
      </div>
    </div>
  );
}
